import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FileText, Coins, User, Menu, X, LogOut, Brain, Accessibility
} from "lucide-react";
import { HeaderLanguageSelector } from "./HeaderLanguageSelector";
import { LoPublicHeader } from "./LoPublicHeader";
import { useAuthStore } from "@/stores/auth.store";
import { useState, useEffect } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import { isLyrraOne } from "@/lib/platform";
import { useLyrraOneRegional } from "@/hooks/useLyrraOneRegional";
import { usePublicWebinarsNavVisible } from "@/lib/public-home-webinars";
import {
  canAccessTrainerDashboard,
  getLyrraOneUserDashboardHref,
} from "@/lib/auth-role";

export function ModernHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { t } = useTranslation();
  const { tx: hdr } = useLyrraOneRegional('lyrraoneHeader');

  const formatUserDisplayName = () => {
    if (!user) return '';
    if (user.firstName || user.lastName) {
      const formatFirstName = (name: string) => {
        return name.split(/[-\s]/).map(part =>
          part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        ).join('-');
      };
      const firstName = user.firstName ? formatFirstName(user.firstName) : '';
      const lastName = user.lastName ? user.lastName.toUpperCase() : '';
      return `${firstName} ${lastName}`.trim();
    }
    return user.email?.split('@')[0] || user.email || '';
  };
  const userDisplayName = formatUserDisplayName();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".user-menu-container")) {
        setIsUserMenuOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const lyrraOne = isLyrraOne();
  const showWebinarsNav = usePublicWebinarsNavVisible();

  if (lyrraOne) {
    const isActive = (path: string) => {
      if (path === '/store') return location.pathname.startsWith('/store');
      if (path === '/webinaires') return location.pathname === '/webinaires';
      return location.pathname === path;
    };

    const lyrraOneDashboardHref = user ? getLyrraOneUserDashboardHref(user.role) : '/dashboard';
    const lyrraOneDashboardLabel = user && !canAccessTrainerDashboard(user.role)
      ? hdr('user.learnerDashboard')
      : hdr('user.dashboard');

    return (
      <LoPublicHeader
        copy={{
          nav: {
            solutions: hdr('nav.solutions'),
            features: hdr('nav.features'),
            marketplace: hdr('nav.marketplace'),
            demo: hdr('nav.demo'),
            blog: hdr('nav.blog'),
            pricing: hdr('nav.pricing'),
            contact: hdr('nav.contact'),
            accessibility: hdr('nav.accessibility'),
            webinars: hdr('nav.webinars'),
          },
          auth: {
            login: hdr('auth.login'),
            register: hdr('auth.register'),
          },
          user: {
            dashboard: lyrraOneDashboardLabel,
            profile: hdr('user.profile'),
            logout: hdr('user.logout'),
          },
        }}
        isActive={isActive}
        renderLink={({ href, className, children, onClick, 'aria-current': ariaCurrent }) => (
          <Link to={href} className={className} onClick={onClick} aria-current={ariaCurrent}>
            {children}
          </Link>
        )}
        langSelector={<HeaderLanguageSelector />}
        showWebinarsNav={showWebinarsNav}
        user={user ? {
          displayName: userDisplayName,
          email: user.email,
          dashboardHref: lyrraOneDashboardHref,
          profileHref: canAccessTrainerDashboard(user.role) ? '/profile' : '/learner?tab=profile',
          onLogout: () => void handleLogout(),
        } : null}
        loginHref="/login"
        registerHref="/register"
      />
    );
  }

  const authLoginLabel = t('publicHeader.auth.login');
  const authRegisterLabel = t('publicHeader.auth.register');
  const userDashboardLabel = t('publicHeader.user.dashboard');
  const userProfileLabel = t('publicHeader.user.profile');
  const userLogoutLabel = t('publicHeader.user.logout');

  const navLinks = [
    { name: t('publicHeader.nav.home'), href: "/", icon: FileText, protected: false },
    { name: t('publicHeader.nav.accessibility'), href: "/accessibility", icon: Accessibility, protected: false },
    { name: t('publicHeader.nav.useCases'), href: "/use-cases", icon: Brain, protected: false },
    { name: t('publicHeader.nav.pricing'), href: "/pricing", icon: Coins, protected: false },
    { name: t('publicHeader.nav.about'), href: "/about", icon: Brain, protected: false },
    { name: t('publicHeader.nav.contact'), href: "/contact", icon: FileText, protected: false }
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <header
      className="sticky top-0 z-50 w-full bg-[#F8FAFC] dark:bg-[#F8FAFC]"
      data-marketing-header
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-3">
        <div className="flex justify-between items-center h-20 md:h-22 rounded-2xl border border-[#E8EDF5] bg-white/95 px-4 sm:px-6 backdrop-blur-xl shadow-[0_12px_40px_rgba(12,28,56,0.08)]">
          <Link to="/" className="flex items-center gap-2 group">
              <div className="flex flex-col">
                <img src="/Logo.png" alt="LyrraOne" className="h-14 md:h-16 w-auto" />
              </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden xl:flex items-center gap-1 rounded-2xl border border-slate-200 bg-slate-50/80 p-1" aria-label="Navigation principale">
            {navLinks
              .filter(link => !link.protected || user)
              .map((link) => {
                const Icon = link.icon;
                const active = isActive(link.href);
                return (
                  <Link
                    key={link.href}
                    to={link.href}
                    aria-current={active ? 'page' : undefined}
                    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 group ${
                      active
                        ? "bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,white)] text-[#0C1C38] border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,white)]"
                        : "text-[#475569] hover:text-[#0F172A] hover:bg-white"
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 ${
                        active
                          ? "text-[#0C1C38]"
                          : "group-hover:text-[#19335E]"
                      } transition-colors`}
                    />
                    <span>{link.name}</span>
                  </Link>
                );
              })}
            <HeaderLanguageSelector />
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="xl:hidden">
              <HeaderLanguageSelector
                buttonClassName="min-h-[44px] min-w-[44px] !p-2 rounded-xl bg-[#F8FAFC] hover:bg-[#DCE7F7]"
              />
            </div>
            {user ? (
              <div className="user-menu-container relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="menu"
                  className="flex items-center gap-2 p-2 rounded-xl bg-[#F8FAFC] hover:bg-[#E2E8F0]/50 transition-colors"
                >
                  <User className="h-4 w-4 text-[#475569]" />
                  <span className="hidden sm:block text-sm text-[#475569]">{userDisplayName}</span>
                </button>

                {isUserMenuOpen && (
                  <div className="absolute end-0 mt-2 w-56 rounded-2xl bg-white border border-[#E2E8F0] py-2 shadow-xl z-[9999]">
                    <div className="px-4 py-3 border-b border-[#E2E8F0]">
                      <p className="text-sm font-medium text-[#0F172A]">{userDisplayName}</p>
                      <p className="text-xs text-[#64748B]">{user.email}</p>
                    </div>
                    <Link
                      to="/dashboard"
                      className="flex items-center px-4 py-2 text-sm text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <Brain className="h-4 w-4 me-3" />
                      {userDashboardLabel}
                    </Link>
                    <Link
                      to="/profile"
                      className="flex items-center px-4 py-2 text-sm text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="h-4 w-4 me-3" />
                      {userProfileLabel}
                    </Link>
                    <div className="border-t border-[#E2E8F0] mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-4 py-2 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <LogOut className="h-4 w-4 me-3" />
                        {userLogoutLabel}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" className="px-4 py-2 text-sm font-medium text-[#475569] hover:text-[#0F172A] transition-colors">
                  {authLoginLabel}
                </Link>
                <Link to="/register" className="px-4 py-2 text-sm font-semibold rounded-2xl text-white transition-all duration-200 shadow-lg hover:shadow-xl bg-[#1D4ED8] hover:bg-[#1E3A8A] shadow-[#1D4ED8]/25">
                  {authRegisterLabel}
                </Link>
              </div>
            )}

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="xl:hidden p-2 rounded-xl bg-[#F8FAFC] hover:bg-[#E2E8F0]/50 transition-colors"
              aria-expanded={isMenuOpen}
              aria-controls="mobile-menu"
              aria-label="Menu de navigation"
            >
              {isMenuOpen ? <X className="h-5 w-5 text-[#475569]" /> : <Menu className="h-5 w-5 text-[#475569]" />}
            </button>
          </div>
        </div>

        {isMenuOpen && (
          <div
            id="mobile-menu"
            className="xl:hidden mt-3 max-h-[calc(100vh-7.5rem)] overflow-y-auto rounded-2xl border border-[#E2E8F0] bg-white py-4 shadow-lg"
          >
            <nav className="flex flex-col space-y-2" aria-label="Navigation mobile">
              {navLinks
                .filter(link => !link.protected || user)
                .map((link) => {
                  const Icon = link.icon;
                  const active = isActive(link.href);
                  return (
                    <Link
                      key={link.href}
                      to={link.href}
                      aria-current={active ? 'page' : undefined}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                        active
                          ? "bg-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_12%,white)] text-[#0C1C38] border border-[color:color-mix(in_srgb,var(--inst-player-primary,#0C1C38)_25%,white)]"
                          : "text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC]"
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Icon className={`h-5 w-5 ${active ? "text-[color:var(--inst-player-primary,#0C1C38)]" : ""}`} />
                      <span className="font-medium">{link.name}</span>
                    </Link>
                  );
                })}
              {!user && (
                <div className="border-t border-[#E2E8F0] pt-4 mt-4">
                  <Link to="/login" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors" onClick={() => setIsMenuOpen(false)}>
                    <User className="h-5 w-5" />
                    <span className="font-medium">{authLoginLabel}</span>
                  </Link>
                  <Link to="/register" className="flex items-center gap-3 px-4 py-3 mt-2 rounded-xl bg-[#1D4ED8] text-white font-medium hover:bg-[#1E3A8A]" onClick={() => setIsMenuOpen(false)}>
                    <User className="h-5 w-5" />
                    <span>{authRegisterLabel}</span>
                  </Link>
                </div>
              )}
              {user && (
                <div className="border-t border-[#E2E8F0] pt-4 mt-4">
                  <div className="px-4 py-3 rounded-xl bg-[#DBEAFE]/30 border border-[#2563EB]/10 mb-3">
                    <p className="text-sm font-medium text-[#0F172A]">{userDisplayName}</p>
                    <p className="text-xs text-[#64748B]">{user.email}</p>
                  </div>
                  <Link to="/dashboard" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors" onClick={() => setIsMenuOpen(false)}>
                    <Brain className="h-5 w-5" />
                    <span className="font-medium">{userDashboardLabel}</span>
                  </Link>
                  <Link to="/profile" className="flex items-center gap-3 px-4 py-3 rounded-xl text-[#475569] hover:text-[#0F172A] hover:bg-[#F8FAFC] transition-colors" onClick={() => setIsMenuOpen(false)}>
                    <User className="h-5 w-5" />
                    <span className="font-medium">{userProfileLabel}</span>
                  </Link>
                  <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50 transition-colors">
                    <LogOut className="h-5 w-5" />
                    <span className="font-medium">{userLogoutLabel}</span>
                  </button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
